import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Layout, Spin } from "antd";
import { useNavigate, useParams } from "react-router";
import { LESSON, CHAT } from "../../config/constants/api";
import { Post } from "../../config/api/post";
import { Get } from "../../config/api/get";
import swal from "sweetalert";
import dayjs from "dayjs";
import { FaArrowLeft } from "react-icons/fa";
import {
  HiOutlineAcademicCap,
  HiOutlineCalendarDays,
  HiOutlineCurrencyDollar,
  HiOutlineUser,
} from "react-icons/hi2";
import "./lesson-detail.css";

function DetailStat({ label, children }) {
  return (
    <div className="ld-stat">
      <span className="ld-stat-label fontFamily1">{label}</span>
      <p className="ld-stat-value fontFamily1">{children}</p>
    </div>
  );
}

function LessonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState({});
  const [loading, setLoading] = useState(true);
  const user = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.userToken);

  useEffect(() => {
    getLessonDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleApprove = () => {
    swal({
      title: "Are you sure?",
      text: "You want to approve this lesson",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then(async function (isConfirm) {
      if (isConfirm) {
        try {
          Post(LESSON.updateLesson + id, { status: "UPCOMING" }, token)
            .then((response) => {
              setLoading(false);
              if (response?.data?.status) {
                swal("Success!", "Lessson Approved Successfully", "success");
                getLessonDetails();
                setLoading(false);
              } else {
                swal("Oops!", response.data.message, "error");
              }
            })
            .catch(() => {
              setLoading(false);
            });
        } catch (err) {
          console.log(err);
        }
      }
    });
  };

  const createChat = () => {
    try {
      Post(
        CHAT.createChat,
        { student: lesson?.student?._id, coach: user._id },
        token
      )
        .then((response) => {
          if (response?.data?.status) {
            navigate("/chat");
          } else {
            swal("Oops!", response.data.message, "error");
          }
        })
        .catch(() => {
          setLoading(false);
        });
    } catch (err) {
      console.log(err);
    }
  };

  const handleReject = () => {
    swal({
      title: "Are you sure?",
      text: "You want to reject this lesson",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then(async function (isConfirm) {
      if (isConfirm) {
        try {
          Post(LESSON.updateLesson + id, { status: "REJECTED" }, token)
            .then((response) => {
              setLoading(false);
              if (response?.data?.status) {
                swal("Success!", "Lessson Rejected Successfully", "success");
                navigate(-1);
                setLoading(false);
              } else {
                swal("Oops!", response.data.message, "error");
              }
            })
            .catch(() => {
              setLoading(false);
            });
        } catch (err) {
          console.log(err);
        }
      }
    });
  };

  const handleComplete = () => {
    swal({
      title: "Are you sure?",
      text: "You want to complete this lesson",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then(async function (isConfirm) {
      if (isConfirm) {
        try {
          Post(LESSON.updateLesson + id, { status: "COMPLETED" }, token)
            .then((response) => {
              setLoading(false);
              if (response?.data?.status) {
                swal("Success!", "Lessson Completed Successfully", "success");
                navigate(-1);
                setLoading(false);
              } else {
                swal("Oops!", response.data.message, "error");
              }
            })
            .catch(() => {
              setLoading(false);
            });
        } catch (err) {
          console.log(err);
        }
      }
    });
  };

  const getLessonDetails = async () => {
    try {
      const response = await Get(LESSON.getLessonById + id, token);

      if (response?.status) {
        setLesson(response?.data?.lesson);
      } else {
        swal("Error", response?.data?.message, "error");
      }
      setLoading(false);
    } catch (error) {
      console.log(error.message);
      setLoading(false);
    }
  };

  const studentInitials = useMemo(() => {
    const fn = lesson?.student?.firstName;
    const ln = lesson?.student?.lastName;
    if (fn && ln) {
      return `${fn[0]}${ln[0]}`.toUpperCase();
    }
    return "?";
  }, [lesson?.student?.firstName, lesson?.student?.lastName]);

  const subjectLabel = useMemo(() => {
    if (!lesson?.subject) return "";
    return lesson.subject[0].toUpperCase() + lesson.subject.slice(1);
  }, [lesson?.subject]);

  const heroSubtitle = useMemo(() => {
    if (loading) return "Loading session details…";
    if (subjectLabel) {
      return `${subjectLabel} · Review schedule, learner information, and next steps.`;
    }
    return "Review schedule, learner information, and next steps.";
  }, [loading, subjectLabel]);

  const timeSlotsLabel =
    lesson?.slots?.length > 0
      ? lesson.slots.map((item, idx) => (
          <React.Fragment key={item._id ?? idx}>
            {dayjs(item.lessonStartTime).format("hh:mm a") +
              " to " +
              dayjs(item.lessonEndTime).format("hh:mm a")}
          </React.Fragment>
        ))
      : "—";

  return (
    <Layout className="ld-page">
      <section className="ld-hero">
        <div className="ld-wrap">
          <button
            type="button"
            className="ld-back"
            aria-label="Go back"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft />
          </button>

          <span className="ld-kicker">
            <HiOutlineAcademicCap /> LESSON
          </span>
          <h1 className="fontFamily1 ld-hero-title">Lesson Detail</h1>
          <p className="fontFamily1 ld-hero-text">{heroSubtitle}</p>

          {!loading && lesson?.status ? (
            <span className="ld-status" data-status={lesson.status}>
              <HiOutlineCalendarDays />
              {lesson.status}
            </span>
          ) : null}
        </div>
      </section>

      <div className="ld-surface">
        <div className="ld-wrap">
          <div className="ld-card">
            {loading && (
              <div className="ld-loading flex">
                <Spin size="large" />
              </div>
            )}

            {!loading && (
              <>
                <div className="ld-stats">
                  <DetailStat label="Lesson ID">{lesson?.lessonId}</DetailStat>
                  <DetailStat label="No. of Lessons">{lesson?.noOfLesson}</DetailStat>
                  <DetailStat label="Lesson Type">{lesson.lessonType}</DetailStat>
                  <DetailStat label="Service / Subject">{subjectLabel || "—"}</DetailStat>
                  <DetailStat label="Lesson Date">
                    {lesson?.lessonDate
                      ? dayjs(lesson.lessonDate).format("DD/MM/YYYY")
                      : "—"}
                  </DetailStat>
                  <DetailStat label="Lesson Time">{timeSlotsLabel}</DetailStat>
                </div>

                <div className="ld-charge">
                  <HiOutlineCurrencyDollar />
                  <div>
                    <span>Lesson Charge</span>
                    <strong>${lesson.charges}</strong>
                  </div>
                </div>

                <h3 className="ld-section-title">
                  <HiOutlineUser /> Learner Information
                </h3>
                <div className="ld-learner">
                  <div className="ld-avatar fontFamily1" aria-hidden>
                    {studentInitials}
                  </div>
                  <div className="ld-learner-body">
                    <span className="ld-learner-label">Learner Name</span>
                    <strong className="ld-learner-name">
                      {(lesson?.student?.firstName || "") +
                        " " +
                        (lesson?.student?.lastName || "")}
                    </strong>
                  </div>
                </div>

                <div className="ld-actions">
                  {lesson.status === "PENDING" ? (
                    <>
                      <Button
                        type="primary"
                        className="ld-btn-primary"
                        size="large"
                        onClick={handleApprove}
                      >
                        Approve Request
                      </Button>
                      <Button
                        className="ld-btn-outline"
                        size="large"
                        onClick={handleReject}
                      >
                        Reject Request
                      </Button>
                    </>
                  ) : lesson.isPaid ? (
                    lesson.status === "LIVE" ? (
                      <Button
                        type="primary"
                        className="ld-btn-primary"
                        size="large"
                        onClick={handleComplete}
                      >
                        Complete Lesson
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="primary"
                          className="ld-btn-primary"
                          size="large"
                          onClick={createChat}
                        >
                          Start Chat
                        </Button>
                        <Button
                          className="ld-btn-outline"
                          size="large"
                          onClick={() => navigate("/lesson/" + id)}
                        >
                          Add Lesson Url
                        </Button>
                      </>
                    )
                  ) : (
                    <div className="ld-payment-pending">
                      <span className="ld-payment-label">Payment Status</span>
                      <strong>Pending Payment</strong>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default LessonDetail;
